import { useEffect, useState } from 'react';
import { ApiFailure, api, type AuditEntry } from '../../api.ts';
import { Notice } from '../../components/Notice.tsx';
import { copy, formatDateTime } from '../../copy/en-CA.ts';

const c = copy.admin.audit;

/** Turns an entry's details into a short readable line, without inventing meaning. */
function describeDetails(details: Record<string, unknown>): string {
  const parts: string[] = [];
  for (const [k, v] of Object.entries(details)) {
    if (v === null || v === undefined || v === '') continue;
    if (typeof v === 'string' || typeof v === 'number' || typeof v === 'boolean') parts.push(`${k}: ${String(v)}`);
    else parts.push(`${k}: ${JSON.stringify(v)}`);
  }
  return parts.join(' · ');
}

export function AdminAuditPage() {
  const [entries, setEntries] = useState<AuditEntry[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    api.admin
      .audit()
      .then((r) => {
        setEntries(r.entries);
      })
      .catch((err: unknown) => {
        setError(err instanceof ApiFailure ? err.message : copy.errors.unexpected);
      });
  }, []);

  return (
    <>
      <div className="page-title">
        <h1>{c.title}</h1>
        <p>{c.intro}</p>
      </div>
      {error ? <Notice kind="error">{error}</Notice> : null}
      {entries?.length === 0 ? (
        <section className="card">
          <p className="empty">{c.empty}</p>
        </section>
      ) : null}
      {entries && entries.length > 0 ? (
        <div className="table-region" role="region" aria-label={c.title} tabIndex={0}>
          <table className="audit">
            <thead>
              <tr>
                <th>{c.columns.when}</th>
                <th>{c.columns.who}</th>
                <th>{c.columns.what}</th>
              </tr>
            </thead>
            <tbody>
              {entries.map((e) => (
                <tr key={e.id}>
                  <td className="muted nowrap">{formatDateTime(e.at)}</td>
                  <td>{e.userName ?? c.system}</td>
                  <td>
                    <strong>{c.actions[e.action] ?? e.action}</strong>
                    <div className="muted small">{describeDetails(e.details)}</div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : null}
    </>
  );
}
